import { useEffect, useState, useCallback } from "react";

// Theme storage. The boot script in index.html reads `luzgrid:theme` and
// resolves it to `light` / `dark` synchronously before React hydrates so
// there's no flash. We mirror to the legacy `lz_crm_theme` key so already-
// deployed builds keep their preference on first load after upgrade.
type Theme = "light" | "dark" | "system";
const KEY = "luzgrid:theme";
const LEGACY_KEY = "lz_crm_theme";

const resolve = (theme: Theme): "light" | "dark" => {
  if (theme === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return theme;
};

const apply = (theme: Theme) => {
  const root = document.documentElement;
  const resolved = resolve(theme);
  const isDark = resolved === "dark";
  // `data-theme` drives the CSS-variable swap (src/index.css).
  // `.dark` class stays in lockstep so Tailwind `dark:` utilities still hit.
  root.setAttribute("data-theme", resolved);
  root.classList.toggle("dark", isDark);
  root.style.colorScheme = resolved;
};

const readStoredTheme = (): Theme => {
  if (typeof window === "undefined") return "dark";
  const v =
    (localStorage.getItem(KEY) as Theme | null) ??
    (localStorage.getItem(LEGACY_KEY) as Theme | null);
  // Master Prompt §1.1: dark is the default.
  return v ?? "dark";
};

export function useDarkMode() {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());

  useEffect(() => {
    apply(theme);
    if (theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const onChange = () => apply("system");
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    localStorage.setItem(KEY, t);
    localStorage.setItem(LEGACY_KEY, t); // mirror — back-compat
    setThemeState(t);
  }, []);

  const toggle = useCallback(() => {
    // 2-state toggle when invoked directly: dark ⇄ light. The dropdown
    // still exposes the `system` option for users who want OS-following.
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  return { theme, setTheme, toggle };
}

/** Eagerly apply theme before React hydrates. */
export function bootTheme() {
  if (typeof window === "undefined") return;
  apply(readStoredTheme());
}
